import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemSecondaryAction, ListItemText, TextField } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import React, { useState } from "react";

import { createInvite } from "modelActions/OrgUserInvite";
import { AppUser } from "models/AppUser";
import { Org } from "models/Org";
import { OrgRoles } from "models/OrgRoles";
import { OrgUser } from "models/OrgUser";
import { createEmailInvite } from "modelActions/OrgUserEmailInvite";
import { useFormik } from 'formik';
import * as yup from 'yup';

type ModalProps<T> = {
    options: T[]
    initialSelection: T[]
    onClose: (selected: T[]) => void
    onCancel?: () => void
    title: string
    createItem: (item: T, isSelected: boolean, toggle: () => void) => React.ReactNode
    warningText?: string
    children?: React.ReactNode
    titlePrefixSlot?: React.ReactNode
    okButton?: (props: { disabled: boolean, onClick: () => void }) => React.ReactNode
}

function removeFromArray<T>(array: T[], itemToRemove: T) {
    return array.filter(item => item !== itemToRemove)
}


function SelectModal<T>({ options, initialSelection, onClose, onCancel, title, createItem, warningText, children, titlePrefixSlot, okButton }: ModalProps<T>) {
    const [selected, setSelected] = useState(initialSelection);

    const isValid = !!selected?.length

    function onComplete() {
        if (isValid) {
            onClose(selected)
        }
    }

    return (
        <Dialog
            open
            onClose={() => onCancel?.()}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">{titlePrefixSlot}{title}</DialogTitle>
            <DialogContent>
                {children}
                {warningText ? (
                    <Alert style={{ width: '100%' }} severity="warning">{warningText}</Alert>
                ) : null}
                <List>
                    {options.map(item => {
                        const isSelected = selected.includes(item)
                        return createItem(item, isSelected, () => {
                            setSelected(isSelected ? removeFromArray(selected, item) : [...selected, item])
                        })
                    })}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onCancel?.()}>Avbryt</Button>
                {okButton
                    ? okButton({ disabled: !isValid, onClick: onComplete })
                    : <Button disabled={!isValid} onClick={onComplete} color="primary" autoFocus>Ok</Button>
                }
            </DialogActions>
        </Dialog>
    )
}

type InviteOrgUserModalBaseProps<T> = {
    orgUser: OrgUser
    onClose: (value: T) => void
    onCancel?: () => void
    children?: React.ReactNode
    titlePrefixSlot?: React.ReactNode
    okButton?: (props: { disabled: boolean, onClick: () => void }) => React.ReactNode
}

type InviteOrgUserModalProps = {
    org: Org
    appUser: AppUser
    orgUser: OrgUser
    inviteUser: AppUser
    setInviteUser: (value: null) => void
}

type InviteOrgUserWithEmailModalProps = {
    org: Org
    appUser: AppUser
    orgUser: OrgUser
    titlePrefixSlot?: React.ReactNode
    onClose?: () => void
    onCancel?: () => void
}

export function InviteOrgUserModalBase({
    orgUser,
    onClose,
    onCancel,
    children,
    okButton,
    titlePrefixSlot,
}: InviteOrgUserModalBaseProps<OrgRoles.OrgRole[]>) {
    return (

        <SelectModal<OrgRoles.OrgRole>
            title="Velg roller"
            titlePrefixSlot={titlePrefixSlot}
            okButton={okButton}
            onCancel={onCancel}
            warningText="NB! Invitasjoner er kun gyldige i 24 timer"
            onClose={onClose}
            initialSelection={[OrgRoles.FlatEnum.WORKER]}
            options={orgUser.orgRoles.map[OrgRoles.FlatEnum.OWNER] ? [OrgRoles.FlatEnum.WORKER, OrgRoles.FlatEnum.OWNER] : [OrgRoles.FlatEnum.WORKER]}
            createItem={(role, isSelected, toggleSelected) => {
                const buttonId = `${role}_button`
                return (
                    <ListItem key={role} button onClick={toggleSelected}>
                        <ListItemText
                            id={buttonId}
                            primary={OrgRoles.Flat[role].name}
                            secondary={OrgRoles.Flat[role].description}
                        />
                        <ListItemSecondaryAction>
                            <Checkbox
                                edge="end"
                                onChange={toggleSelected}
                                checked={isSelected}
                                inputProps={{ 'aria-labelledby': buttonId }}
                            />
                        </ListItemSecondaryAction>
                    </ListItem>
                )
            }}
        >{children}</SelectModal>
    )
}

export function InviteOrgUserModal({
    org,
    orgUser,
    appUser,
    inviteUser,
    setInviteUser,
}: InviteOrgUserModalProps) {
    const { enqueueSnackbar } = useSnackbar()
    return (
        <InviteOrgUserModalBase
            orgUser={orgUser}
            onCancel={() => setInviteUser(null)}
            onClose={(roles) => {
                createInvite(org, orgUser, appUser, inviteUser, roles, enqueueSnackbar)
                setInviteUser(null)
            }}
        />
    )
}

export function InviteOrgUserWithEmailModal({
    org,
    orgUser,
    appUser,
    onClose,
    onCancel,
    titlePrefixSlot,
}: InviteOrgUserWithEmailModalProps) {
    const { enqueueSnackbar } = useSnackbar()
    const formik = useFormik({
        initialValues: {
            email: '',
        },
        validateOnMount: true,
        validationSchema: yup.object({
            email: yup.string().email("Må være en gyldig E-post").max(255).required("Må fylles ut"),
        }),
        onSubmit: async () => {
        },
    })
    return (
        <InviteOrgUserModalBase
            titlePrefixSlot={titlePrefixSlot}
            orgUser={orgUser}
            okButton={(okButtonProps) => (
                <Button
                    disabled={okButtonProps.disabled || !formik.isValid}
                    onClick={okButtonProps.onClick}
                    color="primary"
                    autoFocus
                >Ok</Button>
            )}
            onCancel={onCancel}
            onClose={(roles) => {
                createEmailInvite(org, orgUser, appUser, formik.values.email, roles, enqueueSnackbar)
                onClose?.()
            }}
        >
            <form onSubmit={formik.handleSubmit}>
                <TextField
                    autoFocus
                    fullWidth
                    type="email"
                    label="E-post"
                    variant="outlined"
                    id="email"
                    aria-label="email"
                    name="email"
                    onBlur={formik.handleBlur}
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    error={formik.touched.email && !!formik.errors.email}
                    helperText={formik.touched.email && formik.errors.email}
                />
            </form>
            <Box mt={2} />
        </InviteOrgUserModalBase>
    )
}
